/**
 * Modal para crear/editar un evento
 */

import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select } from "@/components/ui";
import {
  DateTimePickerField,
  DateTimePicker,
} from "@/components/ui/DatePicker";
import {
  X,
  Loader2,
  Calendar,
  MapPin,
  DollarSign,
  Plus,
  Trash2,
  ImagePlus,
  Image,
  Clock,
  ChevronDown,
  ChevronUp,
  MapPinned,
  Search,
  Upload,
} from "lucide-react";
import { useCreateEvent, useUpdateEvent } from "@/hooks/useEvents";
import { useDistricts, useChurches } from "@/hooks/useOrganization";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { Timestamp } from "firebase/firestore";
import { uploadEventImage, validateImageFile } from "@/lib/services/storage";
import type {
  Event,
  ClubType,
  EventType,
  EventLevel,
  EventPricing,
  ConsentChecklistItemId,
  EventConsentChecklistConfig,
} from "@/types";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

// Convierte Date a string compatible con input datetime-local (hora LOCAL, no UTC)
const toLocalDatetimeString = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Atajo: timestamp → string para datetime-local
const tsToLocal = (timestamp: unknown): string => {
  if (!timestamp) return "";
  return toLocalDatetimeString(toSafeDate(timestamp));
};

const eventSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  description: z
    .string()
    .min(10, "La descripción debe tener al menos 10 caracteres"),
  eventType: z.string().min(1, "Selecciona un tipo de evento"),
  level: z.string().min(1, "Selecciona el nivel organizador"),
  startDate: z.string().min(1, "La fecha de inicio es requerida"),
  endDate: z.string().min(1, "La fecha de fin es requerida"),
  locationName: z.string().min(1, "El nombre del lugar es requerido"),
  locationAddress: z.string().min(1, "La dirección es requerida"),
  locationCity: z.string().min(1, "La ciudad es requerida"),
  registrationStart: z
    .string()
    .min(1, "La fecha de inicio de inscripciones es requerida"),
  registrationEnd: z
    .string()
    .min(1, "La fecha de cierre de inscripciones es requerida"),
  maxCapacity: z.number().optional(),
  requiresApproval: z.boolean(),
  contactName: z.string().optional(),
  contactEmail: z.string().email().optional().or(z.literal("")),
  contactPhone: z.string().optional(),
});

type EventForm = z.infer<typeof eventSchema>;

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizerId: string;
  organizerName: string;
  event?: Event | null;
}

const eventTypes: { value: EventType; label: string }[] = [
  { value: "camporee", label: "🏕️ Camporee" },
  { value: "campamento", label: "⛺ Campamento" },
  { value: "convocatoria", label: "📢 Convocatoria" },
  { value: "entrenamiento", label: "📚 Entrenamiento" },
  { value: "investidura", label: "🎖️ Investidura" },
  { value: "social", label: "🎉 Social" },
  { value: "servicio", label: "🤝 Servicio Comunitario" },
  { value: "otro", label: "📌 Otro" },
];

const eventLevels: { value: EventLevel; label: string }[] = [
  { value: "club", label: "Club" },
  { value: "church", label: "Iglesia" },
  { value: "district", label: "Distrito" },
  { value: "association", label: "Asociación" },
  { value: "union", label: "Unión" },
];

const clubTypeOptions: { value: ClubType; label: string }[] = [
  { value: "aventureros", label: "Aventureros" },
  { value: "conquistadores", label: "Conquistadores" },
  { value: "guias_mayores", label: "Guías Mayores" },
];

const defaultPricing: EventPricing[] = [
  { category: "pathfinder", label: "Miembro", amount: 0 },
  { category: "staff", label: "Staff/Consejero", amount: 0 },
  { category: "guest", label: "Invitado", amount: 0 },
];

export default function EventModal({
  isOpen,
  onClose,
  organizerId,
  organizerName,
  event,
}: EventModalProps) {
  const isEditing = !!event;

  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();

  const { profile } = useAuthStore();
  const { isUnionAdmin, directorOfSection } = usePermissions();

  const [selectedClubTypes, setSelectedClubTypes] = useState<ClubType[]>(
    event?.clubTypesAllowed ||
      (directorOfSection ? [directorOfSection] : ["conquistadores"]),
  );
  const [pricing, setPricing] = useState<EventPricing[]>(
    event?.pricing || defaultPricing,
  );
  // Importación CSV de tipos de entrada
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvError, setCsvError] = useState<string | null>(null);
  // Descuento por distrito
  const [discountedDistrictIds, setDiscountedDistrictIds] = useState<string[]>(
    event?.discountedDistrictIds || [],
  );
  const [discountLabel, setDiscountLabel] = useState<string>(
    event?.discountLabel || "Zona rural",
  );
  // Configuración del check-in de documentos por miembro.
  // Default: todos requeridos (retro-compat con eventos sin la config).
  const [checklistConfig, setChecklistConfig] =
    useState<EventConsentChecklistConfig>(event?.consentChecklistConfig ?? {});
  const [documentsDeadline, setDocumentsDeadline] = useState<string>(
    event?.documentsDeadline ? tsToLocal(event.documentsDeadline) : "",
  );
  const [districtSearch, setDistrictSearch] = useState("");
  const [districtsExpanded, setDistrictsExpanded] = useState(false);
  const {
    data: allDistricts,
    isLoading: loadingDistricts,
    error: districtsError,
  } = useDistricts();
  const {
    data: allChurches,
    isLoading: loadingChurches,
    error: churchesError,
  } = useChurches();
  // Filtrar iglesias a la asociación del usuario actual (excepto union admin que ve todas).
  const scopedChurches = useMemo(() => {
    if (!allChurches) return [];
    if (isUnionAdmin || !profile?.scopeId) return allChurches;
    return allChurches.filter((c) => c.associationId === profile.scopeId);
  }, [allChurches, isUnionAdmin, profile?.scopeId]);
  // Fallback: si la colección /districts está vacía (sólo se sembraron iglesias),
  // derivamos la lista de distritos a partir de los churches.
  const districtOptions = useMemo(() => {
    // Si la colección /districts ya existe, filtrarla por asociación del usuario
    // usando el cruce con churches scoped (cuando aplica).
    if (allDistricts && allDistricts.length > 0) {
      if (isUnionAdmin || !profile?.scopeId) {
        return allDistricts.map((d) => ({ id: d.id, name: d.name }));
      }
      const scopedIds = new Set(
        scopedChurches
          .map(
            (c) =>
              (c.districtId as string | undefined) ||
              (c.alfoliDistrictId as string | undefined),
          )
          .filter((x): x is string => !!x),
      );
      return allDistricts
        .filter((d) => scopedIds.has(d.id))
        .map((d) => ({ id: d.id, name: d.name }));
    }
    const map = new Map<string, string>();
    for (const c of scopedChurches) {
      const cAny = c as unknown as Record<string, unknown>;
      const did =
        (cAny.districtId as string | undefined) ||
        (cAny.alfoliDistrictId as string | undefined) ||
        (cAny.distritoId as string | undefined) ||
        (cAny.district_id as string | undefined) ||
        (cAny.district as string | undefined);
      const dname =
        (cAny.districtName as string | undefined) ||
        (cAny.distritoNombre as string | undefined) ||
        (cAny.district_name as string | undefined) ||
        (typeof did === "string" ? did : undefined);
      if (did && !map.has(did)) {
        map.set(did, dname ?? did);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDistricts, scopedChurches, isUnionAdmin, profile?.scopeId]);
  const filteredDistricts = districtOptions.filter((d) =>
    !districtSearch
      ? true
      : d.name.toLowerCase().includes(districtSearch.toLowerCase()),
  );
  // Debug logs (descuento por distrito)
  useEffect(() => {
    const sample = (allChurches ?? [])[0] as unknown as
      | Record<string, unknown>
      | undefined;
    const sampleKeys = sample ? Object.keys(sample) : [];
    // Inspecciona claves que parezcan referirse al distrito
    const districtLikeKeys = sampleKeys.filter((k) =>
      /district|distrito/i.test(k),
    );
    const districtLikeValues: Record<string, unknown> = {};
    for (const k of districtLikeKeys) districtLikeValues[k] = sample?.[k];
    // eslint-disable-next-line no-console
    console.log("[EventModal·distritos]", {
      loadingDistricts,
      districtsError,
      allDistrictsCount: allDistricts?.length ?? null,
      allDistrictsSample: (allDistricts ?? []).slice(0, 3),
      loadingChurches,
      churchesError,
      allChurchesCount: allChurches?.length ?? null,
      churchesWithDistrictId: (allChurches ?? []).filter((c) => !!c.districtId)
        .length,
      sampleChurchKeys: sampleKeys,
      sampleChurchDistrictLike: districtLikeValues,
      sampleChurchFull: sample,
      districtOptionsCount: districtOptions.length,
      districtOptionsSample: districtOptions.slice(0, 5),
      filteredCount: filteredDistricts.length,
      districtSearch,
    });
  }, [
    loadingDistricts,
    districtsError,
    allDistricts,
    loadingChurches,
    churchesError,
    allChurches,
    districtOptions,
    filteredDistricts.length,
    districtSearch,
  ]);
  const discountActive = discountedDistrictIds.length > 0;
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    event?.coverImageUrl || null,
  );
  const [imageError, setImageError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EventForm>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      name: event?.name || "",
      description: event?.description || "",
      eventType: event?.eventType || "",
      level: event?.level || "club",
      startDate: event?.startDate ? tsToLocal(event.startDate) : "",
      endDate: event?.endDate ? tsToLocal(event.endDate) : "",
      locationName: event?.location?.name || "",
      locationAddress: event?.location?.address || "",
      locationCity: event?.location?.city || "",
      registrationStart: event?.registrationConfig?.startDate
        ? tsToLocal(event.registrationConfig.startDate)
        : "",
      registrationEnd: event?.registrationConfig?.endDate
        ? tsToLocal(event.registrationConfig.endDate)
        : "",
      maxCapacity: event?.registrationConfig?.maxCapacity,
      requiresApproval: event?.registrationConfig?.requiresApproval ?? true,
      contactName: event?.contactName || "",
      contactEmail: event?.contactEmail || "",
      contactPhone: event?.contactPhone || "",
    },
  });

  useEffect(() => {
    if (event) {
      // Resetear el formulario con los valores del evento
      reset({
        name: event.name || "",
        description: event.description || "",
        eventType: event.eventType || "",
        level: event.level || "club",
        startDate: event.startDate ? tsToLocal(event.startDate) : "",
        endDate: event.endDate ? tsToLocal(event.endDate) : "",
        locationName: event.location?.name || "",
        locationAddress: event.location?.address || "",
        locationCity: event.location?.city || "",
        registrationStart: event.registrationConfig?.startDate
          ? tsToLocal(event.registrationConfig.startDate)
          : "",
        registrationEnd: event.registrationConfig?.endDate
          ? tsToLocal(event.registrationConfig.endDate)
          : "",
        maxCapacity: event.registrationConfig?.maxCapacity,
        requiresApproval: event.registrationConfig?.requiresApproval ?? true,
        contactName: event.contactName || "",
        contactEmail: event.contactEmail || "",
        contactPhone: event.contactPhone || "",
      });
      setSelectedClubTypes(
        // Para director de sección:
        // - Evento NUEVO o que ya incluye SOLO su sección → forzar [directorOfSection]
        // - Evento existente con MÁS secciones (creado por admin/director general)
        //   → preservar tal cual; el director de sección no puede editarlo abajo
        //   (chips quedan disabled), evitando reducir el alcance accidentalmente.
        directorOfSection &&
          event.clubTypesAllowed.length === 1 &&
          event.clubTypesAllowed[0] === directorOfSection
          ? [directorOfSection]
          : event.clubTypesAllowed,
      );
      setPricing(event.pricing);
      setDiscountedDistrictIds(event.discountedDistrictIds || []);
      setDiscountLabel(event.discountLabel || "Zona rural");
      setChecklistConfig(event.consentChecklistConfig ?? {});
      setDocumentsDeadline(
        event.documentsDeadline ? tsToLocal(event.documentsDeadline) : "",
      );
      setCoverImage(null);
      setCoverImagePreview(event.coverImageUrl || null);
    } else {
      // Resetear a valores vacíos para nuevo evento
      reset({
        name: "",
        description: "",
        eventType: "",
        level: "club",
        startDate: "",
        endDate: "",
        locationName: "",
        locationAddress: "",
        locationCity: "",
        registrationStart: "",
        registrationEnd: "",
        maxCapacity: undefined,
        requiresApproval: true,
        contactName: "",
        contactEmail: "",
        contactPhone: "",
      });
      setSelectedClubTypes([directorOfSection ?? "conquistadores"]);
      setPricing([
        { category: "pathfinder", label: "Miembro", amount: 0 },
        { category: "staff", label: "Staff/Consejero", amount: 0 },
        { category: "guest", label: "Invitado", amount: 0 },
      ]);
      setDiscountedDistrictIds([]);
      setDiscountLabel("Zona rural");
      setChecklistConfig({});
      setDocumentsDeadline("");
      setCoverImage(null);
      setCoverImagePreview(null);
    }
  }, [event, reset]);

  const toggleClubType = (type: ClubType) => {
    // Director acotado a una sección no puede crear/editar eventos para otras
    // secciones (incluso si está editando un evento existente que las incluía).
    if (directorOfSection) return;
    setSelectedClubTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const updatePricing = (
    index: number,
    field: keyof EventPricing,
    value: string | number | Timestamp | undefined,
  ) => {
    setPricing((prev) =>
      prev.map((p, i) => {
        if (i !== index) return p;
        const updated = { ...p, [field]: value };
        // Si se actualiza el label, también actualizar la categoría
        if (field === "label" && typeof value === "string") {
          updated.category =
            value
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[^a-z0-9_]/g, "") || `custom_${index}`;
        }
        return updated;
      }),
    );
  };

  const toggleEarlyBird = (index: number) => {
    setPricing((prev) =>
      prev.map((p, i) => {
        if (i !== index) return p;
        if (p.earlyBirdAmount != null) {
          // Desactivar early bird
          const { earlyBirdAmount, earlyBirdDeadline, ...rest } = p;
          return rest as EventPricing;
        } else {
          // Activar early bird con valores default
          return {
            ...p,
            earlyBirdAmount: Math.round(p.amount * 0.8) || 0,
            earlyBirdDeadline: undefined,
          };
        }
      }),
    );
  };

  const addPricingCategory = () => {
    const newCategory = `custom_${Date.now()}`;
    setPricing((prev) => [
      ...prev,
      { category: newCategory, label: "", amount: 0 },
    ]);
  };

  const removePricingCategory = (index: number) => {
    setPricing((prev) => prev.filter((_, i) => i !== index));
  };

  // -------------------------------------------------------------------------
  // Importación CSV de tipos de entrada
  // -------------------------------------------------------------------------
  const parsePricingCsv = (text: string): EventPricing[] => {
    // Soporta separadores , o ;  y comillas simples para valores con coma
    const lines = text
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) throw new Error("El CSV está vacío");

    const splitRow = (row: string): string[] => {
      const sep = row.includes(";") && !row.includes(",") ? ";" : ",";
      const out: string[] = [];
      let cur = "";
      let inQuotes = false;
      for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '"') {
          if (inQuotes && row[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === sep && !inQuotes) {
          out.push(cur);
          cur = "";
        } else {
          cur += ch;
        }
      }
      out.push(cur);
      return out.map((c) => c.trim());
    };

    const header = splitRow(lines[0]).map((h) =>
      h.toLowerCase().replace(/\s+/g, ""),
    );
    const idx = (name: string) => header.indexOf(name.toLowerCase());
    const iLabel = idx("label");
    const iAmount = idx("amount");
    if (iLabel === -1 || iAmount === -1) {
      throw new Error(
        'El CSV debe tener al menos las columnas "label" y "amount" en la primera fila.',
      );
    }
    const iEarly = idx("earlyBirdAmount");
    const iEarlyDl = idx("earlyBirdDeadline");
    const iDisc = idx("discountedAmount");
    const iDiscEarly = idx("discountedEarlyBirdAmount");

    const toNum = (v: string | undefined): number | undefined => {
      if (v == null || v === "") return undefined;
      const n = parseInt(v.replace(/[.\s]/g, "").replace(",", "."));
      return isNaN(n) ? undefined : n;
    };
    const toDate = (v: string | undefined): Timestamp | undefined => {
      if (!v) return undefined;
      const d = new Date(v);
      return isNaN(d.getTime()) ? undefined : Timestamp.fromDate(d);
    };

    const rows: EventPricing[] = [];
    for (let r = 1; r < lines.length; r++) {
      const cols = splitRow(lines[r]);
      const label = cols[iLabel];
      if (!label) continue;
      const amount = toNum(cols[iAmount]);
      if (amount == null) {
        throw new Error(`Fila ${r + 1}: "amount" inválido para "${label}"`);
      }
      const category =
        label
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "") || `custom_${r}`;
      const item: EventPricing = { category, label, amount };
      if (iEarly !== -1) {
        const v = toNum(cols[iEarly]);
        if (v != null) item.earlyBirdAmount = v;
      }
      if (iEarlyDl !== -1) {
        const v = toDate(cols[iEarlyDl]);
        if (v) item.earlyBirdDeadline = v;
      }
      if (iDisc !== -1) {
        const v = toNum(cols[iDisc]);
        if (v != null) item.discountedAmount = v;
      }
      if (iDiscEarly !== -1) {
        const v = toNum(cols[iDiscEarly]);
        if (v != null) item.discountedEarlyBirdAmount = v;
      }
      rows.push(item);
    }
    if (rows.length === 0) throw new Error("No se encontraron filas válidas.");
    return rows;
  };

  const handleApplyCsv = () => {
    try {
      const parsed = parsePricingCsv(csvText);
      setPricing(parsed);
      setCsvError(null);
      setShowCsvImport(false);
      setCsvText("");
    } catch (err) {
      setCsvError(err instanceof Error ? err.message : "CSV inválido");
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setImageError(validation.error || "Error al validar imagen");
      return;
    }

    setImageError(null);

    // Materializar el archivo en memoria de inmediato: en móvil el File del
    // selector es solo una referencia al archivo en disco y puede invalidarse
    // antes de subirlo al guardar el evento (NotReadableError). Leer los bytes
    // ahora, con el handle fresco, deja una copia estable.
    let safeFile = file;
    try {
      const buffer = await file.arrayBuffer();
      safeFile = new File([buffer], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } catch (err) {
      console.error("No se pudo leer la imagen seleccionada:", err);
      setImageError("No se pudo leer la imagen. Vuelve a seleccionarla.");
      return;
    }
    setCoverImage(safeFile);

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreview(reader.result as string);
    };
    reader.readAsDataURL(safeFile);
  };

  const removeImage = () => {
    setCoverImage(null);
    setCoverImagePreview(null);
  };

  const onSubmit = async (data: EventForm) => {
    if (selectedClubTypes.length === 0) {
      return;
    }

    // Subir imagen si hay una nueva
    let coverImageUrl = event?.coverImageUrl;
    if (coverImage) {
      setUploadingImage(true);
      try {
        const result = await uploadEventImage(coverImage, event?.id);
        coverImageUrl = result.url;
      } catch (error) {
        console.error("Error uploading image:", error);
        setImageError("Error al subir la imagen");
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }

    const eventData = {
      name: data.name,
      description: data.description,
      eventType: data.eventType as EventType,
      level: data.level as EventLevel,
      organizerId,
      organizerName,
      startDate: Timestamp.fromDate(new Date(data.startDate)),
      endDate: Timestamp.fromDate(new Date(data.endDate)),
      location: {
        name: data.locationName,
        address: data.locationAddress,
        city: data.locationCity,
      },
      clubTypesAllowed: selectedClubTypes,
      registrationConfig: {
        startDate: Timestamp.fromDate(new Date(data.registrationStart)),
        endDate: Timestamp.fromDate(new Date(data.registrationEnd)),
        maxCapacity: data.maxCapacity || undefined,
        allowWaitlist: true,
        requiresApproval: data.requiresApproval,
      },
      pricing,
      discountedDistrictIds: discountActive ? discountedDistrictIds : [],
      discountLabel: discountActive
        ? discountLabel.trim() || undefined
        : undefined,
      // Solo persistir la config si algún ítem fue desactivado; si está vacía
      // omitimos el campo (equivale al default "todo requerido").
      ...(Object.keys(checklistConfig).length > 0
        ? { consentChecklistConfig: checklistConfig }
        : {}),
      ...(documentsDeadline
        ? {
            documentsDeadline: Timestamp.fromDate(new Date(documentsDeadline)),
          }
        : {}),
      status: event?.status || ("draft" as const),
      registeredCount: event?.registeredCount || 0,
      confirmedCount: event?.confirmedCount || 0,
      coverImageUrl,
      contactName: data.contactName || undefined,
      contactEmail: data.contactEmail || undefined,
      contactPhone: data.contactPhone || undefined,
    };

    try {
      if (isEditing && event) {
        await updateMutation.mutateAsync({ id: event.id, data: eventData });
      } else {
        await createMutation.mutateAsync(
          eventData as Omit<
            Event,
            "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
          >,
        );
      }
      reset();
      onClose();
    } catch (error) {
      console.error("Error saving event:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? "Editar Evento" : "Nuevo Evento"}
              </h2>
              <p className="text-sm text-gray-500">Organiza: {organizerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Imagen de portada */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Imagen del Evento
            </h3>

            <div className="flex items-start gap-4">
              {coverImagePreview ? (
                <div className="relative">
                  <img
                    src={coverImagePreview}
                    alt="Preview"
                    className="w-40 h-24 object-cover rounded-xl border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-40 h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors duration-200">
                  <ImagePlus className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">
                    Subir imagen
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}
              <div className="text-sm text-gray-500">
                <p>Imagen de portada del evento (opcional)</p>
                <p className="text-xs">JPG, PNG o WebP. Máx 5MB.</p>
                {imageError && (
                  <p className="text-red-500 mt-1">{imageError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Información básica */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Información del Evento
            </h3>

            <Input
              label="Nombre del Evento *"
              {...register("name")}
              error={errors.name?.message}
              placeholder="Ej: Camporee 2026"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción *
              </label>
              <textarea
                {...register("description")}
                rows={3}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                placeholder="Describe el evento..."
              />
              {errors.description && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Tipo de Evento *"
                {...register("eventType")}
                error={errors.eventType?.message}
              >
                <option value="">Seleccionar...</option>
                {eventTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>

              <Select
                label="Nivel Organizador *"
                {...register("level")}
                error={errors.level?.message}
              >
                {eventLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <DateTimePickerField
                control={control}
                name="startDate"
                label="Fecha y Hora de Inicio *"
                error={errors.startDate?.message}
              />
              <DateTimePickerField
                control={control}
                name="endDate"
                label="Fecha y Hora de Fin *"
                error={errors.endDate?.message}
              />
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Ubicación
            </h3>

            <Input
              label="Nombre del Lugar *"
              {...register("locationName")}
              error={errors.locationName?.message}
              placeholder="Ej: Campamento Los Pinos"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Dirección *"
                {...register("locationAddress")}
                error={errors.locationAddress?.message}
                placeholder="Dirección completa"
              />
              <Input
                label="Ciudad *"
                {...register("locationCity")}
                error={errors.locationCity?.message}
                placeholder="Ciudad"
              />
            </div>
          </div>

          {/* Clubes permitidos */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">
              Tipos de Club Permitidos *
            </h3>
            {directorOfSection && (
              <p className="text-xs text-gray-500">
                Como director de sección, los eventos que creas se restringen
                automáticamente a tu sección.
              </p>
            )}
            {directorOfSection &&
              isEditing &&
              !(
                selectedClubTypes.length === 1 &&
                selectedClubTypes[0] === directorOfSection
              ) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Este evento incluye otras secciones además de la tuya. No
                  puedes modificar las secciones permitidas para no afectar a
                  inscritos de otras secciones. Solicita el cambio al director
                  general o a un admin.
                </div>
              )}
            <div className="flex flex-wrap gap-2">
              {clubTypeOptions.map((option) => {
                const locked =
                  !!directorOfSection && option.value !== directorOfSection;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleClubType(option.value)}
                    disabled={locked}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                      selectedClubTypes.includes(option.value)
                        ? "bg-purple-600 text-white"
                        : locked
                          ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {selectedClubTypes.length === 0 && (
              <p className="text-sm text-red-500">
                Selecciona al menos un tipo de club
              </p>
            )}
          </div>

          {/* Inscripciones */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">
              Configuración de Inscripciones
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <DateTimePickerField
                control={control}
                name="registrationStart"
                label="Inicio de Inscripciones *"
                error={errors.registrationStart?.message}
              />
              <DateTimePickerField
                control={control}
                name="registrationEnd"
                label="Cierre de Inscripciones *"
                error={errors.registrationEnd?.message}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Cupo Máximo (opcional)"
                type="number"
                {...register("maxCapacity", { valueAsNumber: true })}
                placeholder="Sin límite"
              />
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  {...register("requiresApproval")}
                  id="requiresApproval"
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label
                  htmlFor="requiresApproval"
                  className="text-sm text-gray-700"
                >
                  Requiere aprobación
                </label>
              </div>
            </div>
          </div>

          {/* Descuento por distrito */}
          <div className="space-y-3 border border-amber-200 bg-amber-50/40 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <MapPinned className="w-4 h-4 text-amber-600" />
                Descuento por distrito{" "}
                <span className="text-xs font-normal text-gray-500">
                  (opcional)
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setDistrictsExpanded((v) => !v)}
                className="text-xs text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1"
              >
                {discountActive
                  ? `${discountedDistrictIds.length} distrito(s) seleccionado(s)`
                  : "Configurar"}
                {districtsExpanded ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            </div>

            <p className="text-xs text-gray-600">
              Aplica un precio reducido a los clubes cuyo distrito (vía iglesia)
              esté en la lista. Cada categoría podrá tener su propio "Precio
              descuento" en la sección de abajo.
            </p>

            {districtsExpanded && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Etiqueta del descuento (visible al inscribir)
                  </label>
                  <input
                    type="text"
                    value={discountLabel}
                    onChange={(e) => setDiscountLabel(e.target.value)}
                    placeholder="Ej: Zona rural"
                    className="w-full px-3 py-2 border-2 border-amber-200 rounded-xl focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 text-sm"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Distritos con descuento
                    </label>
                    <div className="flex gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() =>
                          setDiscountedDistrictIds(
                            districtOptions.map((d) => d.id),
                          )
                        }
                        className="text-amber-700 hover:underline"
                      >
                        Todos
                      </button>
                      <span className="text-gray-300">·</span>
                      <button
                        type="button"
                        onClick={() => setDiscountedDistrictIds([])}
                        className="text-gray-600 hover:underline"
                      >
                        Ninguno
                      </button>
                    </div>
                  </div>

                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={districtSearch}
                      onChange={(e) => setDistrictSearch(e.target.value)}
                      placeholder="Buscar distrito..."
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                    />
                  </div>

                  <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                    {loadingDistricts || loadingChurches ? (
                      <p className="text-xs text-gray-500 p-3">
                        Cargando distritos…
                      </p>
                    ) : districtsError && churchesError ? (
                      <p className="text-xs text-red-600 p-3">
                        Error: {String(churchesError)}
                      </p>
                    ) : districtOptions.length === 0 ? (
                      <p className="text-xs text-gray-500 p-3">
                        No hay distritos disponibles. Iglesias en tu asociación:{" "}
                        {scopedChurches.length} (de {allChurches?.length ?? 0}{" "}
                        totales).
                      </p>
                    ) : filteredDistricts.length === 0 ? (
                      <p className="text-xs text-gray-500 p-3">
                        Sin distritos que coincidan ({districtOptions.length}{" "}
                        disponibles).
                      </p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {filteredDistricts.map((d) => {
                          const checked = discountedDistrictIds.includes(d.id);
                          return (
                            <li key={d.id}>
                              <label className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-amber-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setDiscountedDistrictIds((prev) =>
                                      e.target.checked
                                        ? [...prev, d.id]
                                        : prev.filter((x) => x !== d.id),
                                    );
                                  }}
                                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="text-gray-700">{d.name}</span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Precios */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Tipos de Entrada y Precios (COP)
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCsvImport((v) => !v);
                    setCsvError(null);
                  }}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-purple-700 transition-colors duration-200"
                  title="Pegar tipos de entrada desde CSV"
                >
                  <Upload className="w-4 h-4" />
                  Importar CSV
                </button>
                <button
                  type="button"
                  onClick={addPricingCategory}
                  className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Agregar tipo
                </button>
              </div>
            </div>

            {showCsvImport && (
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50/40 p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-gray-600">
                    Pega filas CSV con encabezado. Columnas:{" "}
                    <code className="bg-white px-1 rounded">label</code>,{" "}
                    <code className="bg-white px-1 rounded">amount</code>,{" "}
                    <code className="bg-white px-1 rounded">
                      earlyBirdAmount
                    </code>
                    ,{" "}
                    <code className="bg-white px-1 rounded">
                      earlyBirdDeadline
                    </code>{" "}
                    (YYYY-MM-DD HH:mm),{" "}
                    <code className="bg-white px-1 rounded">
                      discountedAmount
                    </code>
                    ,{" "}
                    <code className="bg-white px-1 rounded">
                      discountedEarlyBirdAmount
                    </code>
                    .{" "}
                    <a
                      href="/templates/plantilla-tipos-entrada.csv"
                      download
                      className="text-purple-700 underline hover:text-purple-900"
                    >
                      Descargar plantilla
                    </a>
                  </p>
                </div>
                <textarea
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  rows={6}
                  placeholder={`label,amount,earlyBirdAmount,earlyBirdDeadline,discountedAmount,discountedEarlyBirdAmount\nMiembro,80000,60000,2026-05-30 23:59,40000,30000\nStaff/Consejero,50000,,,25000,\nInvitado,120000,,,,`}
                  className="w-full font-mono text-xs px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200"
                />
                {csvError && <p className="text-xs text-red-600">{csvError}</p>}
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCsvImport(false);
                      setCsvText("");
                      setCsvError(null);
                    }}
                    className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyCsv}
                    disabled={!csvText.trim()}
                    className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reemplazar tipos
                  </button>
                </div>
                <p className="text-[11px] text-gray-500">
                  ⚠️ Esta acción reemplaza todos los tipos de entrada actuales.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {pricing.map((price, index) => (
                <div
                  key={index}
                  className="border-2 border-gray-200 rounded-xl p-3 space-y-3"
                >
                  {/* Fila principal: label + precio normal + (precio descuento si aplica) + eliminar */}
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={price.label}
                      onChange={(e) =>
                        updatePricing(index, "label", e.target.value)
                      }
                      placeholder="Nombre del tipo de entrada"
                      className="flex-1 px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-sm"
                    />
                    <div className="relative w-28">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                        $
                      </span>
                      <input
                        type="number"
                        value={price.amount === 0 ? "" : price.amount}
                        onChange={(e) =>
                          updatePricing(
                            index,
                            "amount",
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value),
                          )
                        }
                        onBlur={(e) => {
                          if (
                            e.target.value === "" ||
                            isNaN(parseInt(e.target.value))
                          ) {
                            updatePricing(index, "amount", 0);
                          }
                        }}
                        placeholder="Normal"
                        title="Precio normal"
                        min="0"
                        className="w-full pl-8 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 text-sm"
                      />
                    </div>
                    {discountActive && (
                      <div className="relative w-28">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          value={
                            price.discountedAmount == null ||
                            price.discountedAmount === 0
                              ? ""
                              : price.discountedAmount
                          }
                          onChange={(e) =>
                            updatePricing(
                              index,
                              "discountedAmount",
                              e.target.value === ""
                                ? undefined
                                : parseInt(e.target.value),
                            )
                          }
                          placeholder="Descuento"
                          title={`Precio para distritos con descuento (${discountLabel})`}
                          min="0"
                          className="w-full pl-8 pr-3 py-2.5 border-2 border-amber-200 bg-amber-50/40 rounded-xl focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 text-sm"
                        />
                      </div>
                    )}
                    {pricing.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePricingCategory(index)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors duration-200"
                        title="Eliminar tipo de entrada"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Toggle early bird */}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => toggleEarlyBird(index)}
                      className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors duration-200 ${
                        price.earlyBirdAmount != null
                          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      <Clock className="w-3.5 h-3.5" />
                      Pronto pago
                      {price.earlyBirdAmount != null ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>

                  {/* Early bird fields */}
                  {price.earlyBirdAmount != null && (
                    <div className="flex flex-wrap items-center gap-3 pl-2 border-l-4 border-emerald-300">
                      <div className="relative w-32">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          value={
                            price.earlyBirdAmount === 0
                              ? ""
                              : price.earlyBirdAmount
                          }
                          onChange={(e) =>
                            updatePricing(
                              index,
                              "earlyBirdAmount",
                              e.target.value === ""
                                ? 0
                                : parseInt(e.target.value),
                            )
                          }
                          placeholder="Precio pronto pago"
                          min="0"
                          className="w-full pl-8 pr-3 py-2 border-2 border-emerald-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200 text-sm"
                        />
                      </div>
                      {discountActive && price.discountedAmount != null && (
                        <div className="relative w-36">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600 text-sm">
                            $
                          </span>
                          <input
                            type="number"
                            value={
                              price.discountedEarlyBirdAmount == null ||
                              price.discountedEarlyBirdAmount === 0
                                ? ""
                                : price.discountedEarlyBirdAmount
                            }
                            onChange={(e) =>
                              updatePricing(
                                index,
                                "discountedEarlyBirdAmount",
                                e.target.value === ""
                                  ? undefined
                                  : parseInt(e.target.value),
                              )
                            }
                            placeholder="Descuento p. pago"
                            title="Precio pronto pago para distritos con descuento"
                            min="0"
                            className="w-full pl-8 pr-3 py-2 border-2 border-amber-200 bg-amber-50/40 rounded-xl focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 text-sm"
                          />
                        </div>
                      )}
                      <span className="text-xs text-gray-500">hasta</span>
                      <DateTimePicker
                        value={
                          price.earlyBirdDeadline
                            ? tsToLocal(price.earlyBirdDeadline)
                            : ""
                        }
                        onChange={(v) =>
                          updatePricing(
                            index,
                            "earlyBirdDeadline",
                            v ? Timestamp.fromDate(new Date(v)) : undefined,
                          )
                        }
                        placeholder="Fecha límite"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              Define los tipos de entrada disponibles. Activa "Pronto pago" para
              ofrecer un precio especial antes de cierta fecha.
            </p>
          </div>

          {/* Documentos del check-in por miembro */}
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900">
                Documentos requeridos en el check-in
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Marca solo los documentos que los miembros inscritos deben subir
                en la plataforma. Los ítems desmarcados no bloquean la
                finalización del check-in (útil cuando se entregan en físico).
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(
                [
                  {
                    id: "idDocumentFront",
                    label: "Documento de identidad (anverso)",
                  },
                  {
                    id: "idDocumentBack",
                    label: "Documento de identidad (reverso)",
                  },
                  { id: "epsCertificate", label: "Certificado de EPS" },
                  {
                    id: "medicalAndTravel",
                    label: "Datos médicos y del viaje",
                  },
                  {
                    id: "dataConsent",
                    label: "Autorización de tratamiento de datos (firma)",
                  },
                  {
                    id: "participationConsent",
                    label: "Autorización de participación firmada",
                  },
                  { id: "participantPhoto", label: "Foto del participante" },
                ] as { id: ConsentChecklistItemId; label: string }[]
              ).map((item) => {
                const isRequired = checklistConfig[item.id] !== false;
                return (
                  <label
                    key={item.id}
                    className="flex items-start gap-2 text-sm p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={isRequired}
                      onChange={(e) => {
                        setChecklistConfig((prev) => ({
                          ...prev,
                          [item.id]: e.target.checked,
                        }));
                      }}
                    />
                    <span className="text-gray-700">{item.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="pt-2">
              <label className="block text-sm text-gray-700 mb-1">
                Fecha límite de documentos (opcional)
              </label>
              <input
                type="datetime-local"
                value={documentsDeadline}
                onChange={(e) => setDocumentsDeadline(e.target.value)}
                className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se muestra a los clubes como recordatorio para completar los
                documentos anexos y del check-in.
              </p>
            </div>
          </div>

          {/* Contacto */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">
              Información de Contacto (opcional)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Nombre"
                {...register("contactName")}
                placeholder="Responsable"
              />
              <Input
                label="Email"
                type="email"
                {...register("contactEmail")}
                placeholder="correo@ejemplo.com"
              />
              <Input
                label="Teléfono"
                {...register("contactPhone")}
                placeholder="3001234567"
              />
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                uploadingImage ||
                createMutation.isPending ||
                updateMutation.isPending ||
                selectedClubTypes.length === 0
              }
            >
              {isSubmitting ||
              uploadingImage ||
              createMutation.isPending ||
              updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {uploadingImage ? "Subiendo imagen..." : "Guardando..."}
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  {isEditing ? "Guardar Cambios" : "Crear Evento"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
